import React, { useEffect, useState } from "react";
import Select from "../../../form/select/Select";
import useForm from "../../../../hooks/useForm";
import Button from "../../../form/Button";
import { postAddEdit, postDetails } from "../../../../services/postService";
import { useMutation, useQuery } from "@tanstack/react-query";
import Input from "../../../form/Input";
import { toast } from "react-toastify";
import { taxonomyList } from "../../../../services/taxonomyService";
import Editor from "../../../elements/tiptap-editor/Editor";
import DatePicker from "../../../form/DatePicker";
import Textarea from "../../../form/Textarea";
import Tags from "../../../form/Tags";
import { useNavigate, useParams } from "react-router-dom";
import SelectImageGallery from "../../../form/SelectImageGallery";
import SelectSingleImage from "../../../form/SelectSingleImage";
import FlexibleSelect from "../../../form/select/FlexibleSelect";

const validation = {
  title: { required: true, message: "title is required" },
  content: { required: true, message: "content is required" },
};

const ManageProduct = ({ getResponse = () => {}, close = () => {} }) => {
  const params = useParams();
  const navigate = useNavigate();
  const id = params?.id;
  const [tags, setTags] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [featuredImage, setFeaturedImage] = useState("");
  const { values, handleChange, handleSubmit, errors, setFieldsValue, resetField, formValidate } = useForm({
    validationRules: validation,
  });
  const [categories, setCategories] = useState([]);

  const { data: details } = useQuery({
    queryKey: ["product-details", id],
    queryFn: async () => {
      const res = await postDetails({ id: id });
      return res?.data;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (details?._id) {
      setFieldsValue(details);
      setCategories(details?.categories);
      setFeaturedImage(details?.featuredImage);
      setSelectedMedia(details?.mediaGallery);
      setTags(
        details.tagDetails.map((item) => ({
          label: item.title,
          _id: item._id,
        }))
      );
    }
  }, [details]);

  const {
    mutate,
    error,
    isPending: isLoading,
  } = useMutation({
    mutationFn: async (val) => {
      const media = selectedMedia?.reduce((acc, item, index) => {
        acc[`mediaGallery[${index}]`] = item?._id;
        return acc;
      }, {});

      const tag = tags?.reduce((acc, tag, index) => {
        acc[`tags[${index}]`] = tag;
        return acc;
      }, {});

      // const categories = val?.categories?.reduce((acc, category, index) => {
      //   acc[`categories[${index}]`] = category;
      //   return acc;
      // }, {});

      return await postAddEdit({
        title: val?.title,
        content: val?.content,
        status: val?.status,
        excerpt: val?.excerpt,
        publishDate: val?.publishDate,
        type: val?.type,
        featuredImage: featuredImage?._id,
        categories: categories,
        ...(details?._id && { id: details._id }),
        ...media,
        // ...tag,
        // ...categories,
      });
    },

    onSuccess: (res) => {
      getResponse(res);
      if (res?.status === 200 || res?.status === 201) {
        navigate(`/admin/products`);
      }
      return res;
    },
    onError: (error) => {
      console.log(error);
    },
  });
  const { data: categoryData } = useQuery({
    queryKey: ["category"],
    queryFn: async () => {
      const res = await taxonomyList({
        limit: 10000,
        type: "product-category",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const { data: tagsData } = useQuery({
    queryKey: ["tags"],
    queryFn: async () => {
      const res = await taxonomyList({
        limit: 10000,
        type: "product-tag",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  return (
    <>
      <form onSubmit={(event) => handleSubmit(event, mutate)}>
        <div className="flex justify-between items-center w-full px-6 py-4 border-b border-gray-200 bg-white">
          <h1 className="font-semibold text-lg xl:text-3xl text-primary">Manage Products</h1>
          <div className="flex gap-3">
            <Button
              buttonType="submit"
              buttonLabel="Save Product"
              buttonClasses="min-w-28"
              buttonLabelClasses="mx-auto"
              {...(isLoading && {
                loading: true,
                buttonIconPosition: "left",
                isDisable: true,
              })}
            />
            <Button
              buttonLabel="Cancel"
              buttonClasses="min-w-28 !bg-white !text-primary border !border-primary"
              buttonLabelClasses="mx-auto"
              buttonHasLink
              buttonLink="/admin/products"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 p-6">
          <div className="xl:col-span-3 space-y-6">
            <div className="space-y-4 p-4 bg-white rounded-md border border-gray-200">
              <Input
                label="Title"
                inputPlaceholder="Enter title"
                onChange={handleChange}
                inputName="title"
                required={false}
                value={values?.title}
                {...formValidate(errors, "title")}
              />
              <div className="flex flex-col md:flex-row gap-4 p-4 bg-white rounded-md border border-gray-200">
                <div className="w-full md:w-1/2">
                  <Input
                    label="SKU"
                    inputPlaceholder="Enter SKU"
                    onChange={handleChange}
                    inputName="sku"
                    value={values?.sku}
                    {...formValidate(errors, "sku")}
                  />
                </div>
                <div className="w-full md:w-1/2">
                  <Input
                    label="Regular Price"
                    inputPlaceholder="Enter Regular Price"
                    onChange={handleChange}
                    inputName="regulerPrice"
                    value={values?.regulerPrice}
                    {...formValidate(errors, "regulerPrice")}
                  />
                </div>
                <div className="w-full md:w-1/2">
                  <Input
                    label="Sale Price"
                    inputPlaceholder="Enter Sale Price"
                    onChange={handleChange}
                    inputName="salePrice"
                    value={values?.salePrice}
                    {...formValidate(errors, "salePrice")}
                  />
                </div>
              </div>
            </div>

            {/* <div className="flex flex-col md:flex-row gap-4 p-4 bg-white rounded-md border border-gray-200">
              <div className="w-full md:w-1/2">
                <Input
                  label="Regular Price"
                  inputPlaceholder="Enter Regular Price"
                  onChange={handleChange}
                  inputName="regulerPrice"
                  value={values?.regulerPrice}
                  {...formValidate(errors, "regulerPrice")}
                />
              </div>
              <div className="w-full md:w-1/2">
                <Input
                  label="Sale Price"
                  inputPlaceholder="Enter Sale Price"
                  onChange={handleChange}
                  inputName="salePrice"
                  value={values?.salePrice}
                  {...formValidate(errors, "salePrice")}
                />
              </div>
            </div> */}

            <div className="p-4 bg-white rounded-md border border-gray-200 space-y-4">
              <Editor
                label="Content"
                height={200}
                value={values?.content}
                placeholder="Write something"
                onChange={(val) =>
                  handleChange({
                    target: { name: "content", value: val },
                  })
                }
                {...formValidate(errors, "content")}
              />
              <Textarea
                label="Excerpt"
                inputPlaceholder="Write excerpt"
                inputClasses="!h-28"
                inputName="excerpt"
                inputValue={values?.excerpt || ""}
                onChange={handleChange}
              />
            </div>

            <div className="bg-white p-4 rounded-md border border-gray-200">
              <SelectImageGallery
                label="Choose Media"
                values={selectedMedia}
                onChange={(newMedia) => setSelectedMedia(newMedia)}
                isSingle={false}
              />
            </div>

            <div className="flex gap-4 mt-6 xl:hidden">
              <Button
                buttonType="submit"
                buttonLabel="Save Product"
                buttonClasses="min-w-28"
                buttonLabelClasses="mx-auto"
                {...(isLoading && {
                  loading: true,
                  buttonIconPosition: "left",
                  isDisable: true,
                })}
              />
              <Button
                buttonLabel="Cancel"
                buttonClasses="min-w-28 !bg-white !text-primary border !border-primary"
                buttonLabelClasses="mx-auto"
                buttonHasLink
                buttonLink="/admin/products"
              />
            </div>
          </div>

          <div className="xl:col-span-1 space-y-6">
            {/* <div className="border border-gray-200 bg-white rounded-md p-4">
              <SelectSingleImage
                label="Upload Featured Image"
                size="lg"
                value={featuredImage}
                onChange={(img) => setFeaturedImage(img)}
                onRemove={() => setFeaturedImage(null)}
              />
            </div> */}

            <div className="border border-gray-200 bg-white rounded-md p-4 space-y-4">
              <FlexibleSelect
                label="Type"
                placeholder="Choose type"
                onChange={(e) => handleChange({ target: { name: "type", value: e } })}
                selectedValue={values?.type}
                dropdownData={[
                  { name: "Simple", value: "simple" },
                  { name: "Varient", value: "variable" },
                ]}
                {...formValidate(errors, "type")}
              />
              <DatePicker
                label="Publish Date"
                placeholder="Choose publish date"
                position="fixed"
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "date",
                      value: e?.startDate || e?.endDate,
                    },
                  })
                }
                value={{
                  startDate: values?.date,
                  endDate: values?.date,
                }}
                {...formValidate(errors, "date")}
              />
              <FlexibleSelect
                label="Status"
                placeholder="Choose status"
                onChange={(e) => handleChange({ target: { name: "status", value: e } })}
                selectedValue={values?.status}
                dropdownData={[
                  { name: "Active", value: "active" },
                  { name: "Inactive", value: "inactive" },
                  { name: "Draft", value: "draft" },
                  { name: "Private", value: "private" },
                ]}
                {...formValidate(errors, "status")}
              />
              <FlexibleSelect
                label="Select Category"
                isSearch
                dropdownData={categoryData?.data?.map((item) => ({
                  name: item?.title,
                  value: item?._id,
                }))}
                selectedValue={categories}
                onChange={setCategories}
                type="multiple"
                placeholder="Choose categories"
              />
              <Tags
                label="Tags"
                placeholder="Enter tags"
                options={tagsData?.data?.map((item) => ({
                  label: item.title,
                  _id: item._id,
                }))}
                selectedItems={tags}
                setSelectedItems={setTags}
                tagsType="input"
              />
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default ManageProduct;
